import { Unlock } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { relative } from '../lib/format';
import type { LockoutPolicy } from '../lib/types';
import { Tile, UsageBars } from './EmailUsage';
import { useParent } from './ParentContext';

interface Blocked {
  kind: 'parent' | 'student';
  id: string;
  name: string;
  detail: string;
  failed: number;
  state: 'locked' | 'cooldown';
  cooldownUntil: number;
  lastFailedAt: number;
  lastFailedIp: string;
}
interface SigninReport {
  totals: {
    today: number;
    last7Days: number;
    last30Days: number;
    thisMonth: number;
    thisYear: number;
    allTime: number;
    lockoutsLast30Days: number;
    lockoutsAllTime: number;
    cooldownsLast30Days: number;
    since: string | null;
  };
  byDay: { label: string; count: number }[];
  byWeek: { label: string; count: number }[];
  byMonth: { label: string; count: number }[];
  byYear: { label: string; count: number }[];
  byKind: { kind: string; count: number }[];
  blocked: Blocked[];
}

const WHO: Record<string, string> = {
  'parent-failed': 'Parent accounts',
  'student-failed': 'Student Portal accounts',
  'unknown-failed': 'Unknown emails or codes',
};

/** Failed sign-in protection settings (#393), part of the Server settings form. */
export function LockoutSettings({ value, onChange }: { value: LockoutPolicy; onChange: (next: LockoutPolicy) => void }) {
  const set = <K extends keyof LockoutPolicy>(k: K, v: LockoutPolicy[K]) => onChange({ ...value, [k]: v });
  return (
    <div className="stack">
      <label className="check">
        <input type="checkbox" checked={value.enabled} onChange={(e) => set('enabled', e.target.checked)} />
        <span>
          Protect against password guessing
          <span className="muted small block">Failed sign-ins (wrong passwords and two-factor codes) slow down, then lock the account.</span>
        </span>
      </label>
      {value.enabled && (
        <>
          <div className="field-row">
            <label>
              Failures before a cooldown
              <input type="number" min={1} max={50} value={value.cooldownAfter} onChange={(e) => set('cooldownAfter', Number(e.target.value) || 1)} />
            </label>
            <label>
              Cooldown (minutes)
              <input type="number" min={1} max={1440} value={value.cooldownMinutes} onChange={(e) => set('cooldownMinutes', Number(e.target.value) || 1)} />
            </label>
            <label>
              Failures before locking
              <input type="number" min={0} max={100} value={value.lockAfter} onChange={(e) => set('lockAfter', Number(e.target.value) || 0)} />
            </label>
          </div>
          <p className="muted small">
            After {value.cooldownAfter} failures in a row the account waits {value.cooldownMinutes} minutes.
            {value.lockAfter ? ` After ${Math.max(value.lockAfter, value.cooldownAfter + 1)} it’s locked until the password is reset: the parent is emailed a reset link.` : ' It’s never locked.'}{' '}
            Set failures before locking to 0 to never lock. A successful sign-in starts the count again. The messages never reveal whether an email has an account.
          </p>
          <label className="check">
            <input type="checkbox" checked={value.students} onChange={(e) => set('students', e.target.checked)} />
            <span>
              Also for Student Portal sign-ins
              <span className="muted small block">A locked student account unlocks when a parent resets its password.</span>
            </span>
          </label>
          <label className="check">
            <input type="checkbox" checked={value.notifyOwner} onChange={(e) => set('notifyOwner', e.target.checked)} />
            <span>Email the account (or the student’s parents) when it locks</span>
          </label>
          <label className="check">
            <input type="checkbox" checked={value.notifyAdmins} onChange={(e) => set('notifyAdmins', e.target.checked)} />
            <span>Email the admins when an account locks</span>
          </label>
          <p className="muted small">Every cooldown, lock and unlock is also written to the audit log.</p>
        </>
      )}
    </div>
  );
}

/** The sign-in security report (#393): failed sign-ins over time, lockouts, and who's locked right now (with Unlock). Its heading is the Fold around it (#601). */
export function SigninSecurityReport() {
  const { toast } = useParent();
  const [report, setReport] = useState<SigninReport | null>(null);
  const load = useCallback(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    api
      .get<SigninReport>(`/api/admin/signin/report?tz=${encodeURIComponent(tz)}`)
      .then(setReport)
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  useEffect(load, [load]);

  async function unlock(b: Blocked) {
    try {
      await api.post(b.kind === 'parent' ? `/api/admin/parents/${b.id}/unlock` : `/api/admin/children/${b.id}/unlock-student`);
      toast({ text: `${b.name} unlocked.`, tone: 'good' });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  if (!report) return <p className="muted small">Loading sign-in report…</p>;
  const t = report.totals;
  return (
    <div className="stack email-usage">
      <div className="usage-tiles">
        <Tile label="Failed today" value={t.today} />
        <Tile label="Failed, 7 days" value={t.last7Days} />
        <Tile label="Failed, 30 days" value={t.last30Days} />
        <Tile label="Failed, all time" value={t.allTime} />
        <Tile label="Lockouts, 30 days" value={t.lockoutsLast30Days} />
        <Tile label="Lockouts, all time" value={t.lockoutsAllTime} />
      </div>
      <p className="muted small">
        {t.cooldownsLast30Days} cooldown{t.cooldownsLast30Days === 1 ? '' : 's'} in the last 30 days.
        {t.since ? ` Counting since ${new Date(`${t.since}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}.` : ''}
      </p>
      <UsageBars report={report} noun="Failed sign-ins" />
      {report.byKind.length > 0 && (
        <table className="usage-kinds">
          <caption className="muted small">Failed sign-ins by account type, last 30 days</caption>
          <tbody>
            {report.byKind.map((k) => (
              <tr key={k.kind}>
                <td>{WHO[k.kind] ?? k.kind}</td>
                <td className="num">{k.count.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <strong className="small">Locked or cooling down right now</strong>
      {report.blocked.length === 0 ? (
        <p className="muted small">No accounts are locked.</p>
      ) : (
        <ul className="task-list">
          {report.blocked.map((b) => (
            <li key={`${b.kind}:${b.id}`} className="task-row">
              <span className="grow">
                <strong>{b.name}</strong> <span className="muted small">{b.detail}</span>
                <span className={`small block ${b.state === 'locked' ? 'task-overdue' : 'muted'}`}>
                  {b.state === 'locked' ? 'Locked until reset' : `Cooling down until ${new Date(b.cooldownUntil).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`} ·{' '}
                  {b.failed} failed in a row{b.lastFailedAt ? ` · last ${relative(b.lastFailedAt)}` : ''}
                  {b.lastFailedIp ? ` from ${b.lastFailedIp}` : ''}
                </span>
              </span>
              <button className="btn small" onClick={() => unlock(b)}>
                <Unlock size={14} /> Unlock
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
